/**
 * Standalone signup page for the signup-without-payment API.
 * Separate from existing signup flows; does not modify existing code.
 */
import React, { useState, useMemo } from 'react';
import 'react-international-phone/style.css';
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Link,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { GoogleIcon } from '../assets/icons';
import { config } from '../config';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { validateEmail, sanitizeTextInput, validateAndSanitizeName } from '../utils/common.util';
import MuiPhone from '../components/MuiPhone';
import { signupWithoutPayment } from '../services/signupWithoutPayment.service';
import AuthService from '../services/auth.service';
import { Applications } from '../types/enums';
import {
  SIGNUP_WITHOUT_PAYMENT_FREE_TRIAL_PLAN_ID,
  type SignupWithoutPaymentRequest,
  type SignupWithoutPaymentUtmParams,
} from '../types/signupWithoutPayment';

function getUtmParamsFromSearchParams(searchParams: URLSearchParams): SignupWithoutPaymentUtmParams | undefined {
  const utm_source = searchParams.get('utm_source') ?? undefined;
  const utm_medium = searchParams.get('utm_medium') ?? undefined;
  const utm_campaign = searchParams.get('utm_campaign') ?? undefined;
  const utm_content = searchParams.get('utm_content') ?? undefined;
  const utm_term = searchParams.get('utm_term') ?? undefined;
  if (!utm_source && !utm_medium && !utm_campaign && !utm_content && !utm_term) return undefined;
  return {
    ...(utm_source && { utm_source }),
    ...(utm_medium && { utm_medium }),
    ...(utm_campaign && { utm_campaign }),
    ...(utm_content && { utm_content }),
    ...(utm_term && { utm_term }),
  };
}

function validatePassword(password: string): { isValid: boolean; error?: string } {
  if (!password || password.length < 8) {
    return { isValid: false, error: 'Password must be at least 8 characters' };
  }
  if (!/[A-Z]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one uppercase letter' };
  }
  if (!/[a-z]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one lowercase letter' };
  }
  if (!/[0-9]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one number' };
  }
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one special character' };
  }
  return { isValid: true };
}

const SignupWithoutPayment: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const utmParams = useMemo(() => getUtmParamsFromSearchParams(searchParams), [searchParams]);

  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    subdomain: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [subdomainSuggestions, setSubdomainSuggestions] = useState<string[]>([]);

  const [signupResult, setSignupResult] = useState<{
    email: string;
    password: string;
    companyId: string;
    companyName: string;
  } | null>(() => {
    const state = location.state as { email?: string; password?: string; companyId?: string; companyName?: string } | null;
    if (state?.email && state?.password !== undefined) {
      return {
        email: state.email,
        password: state.password ?? '',
        companyId: state.companyId ?? '',
        companyName: state.companyName ?? '',
      };
    }
    return null;
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handlePhoneChange = (value: string) => {
    setFormData((prev) => ({ ...prev, phoneNumber: value }));
    if (formErrors.phoneNumber) setFormErrors((prev) => ({ ...prev, phoneNumber: '' }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    const firstNameValidation = validateAndSanitizeName(formData.firstName, 'First name', true);
    if (!firstNameValidation.isValid) errors.firstName = firstNameValidation.error || 'Invalid first name';
    const lastNameValidation = validateAndSanitizeName(formData.lastName, 'Last name', true);
    if (!lastNameValidation.isValid) errors.lastName = lastNameValidation.error || 'Invalid last name';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else {
      const e = validateEmail(formData.email);
      if (!e.isValid) errors.email = e.error || 'Invalid email';
    }
    if (!formData.password) errors.password = 'Password is required';
    else {
      const p = validatePassword(formData.password);
      if (!p.isValid) errors.password = p.error || 'Invalid password';
    }
    if (formData.phoneNumber.trim() && formData.phoneNumber.trim().length > 20) {
      errors.phoneNumber = 'Phone number must be 20 characters or less';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCheckEmail = async (): Promise<boolean> => {
    const email = formData.email.trim().toLowerCase();
    if (!email || !validateEmail(email).isValid) return false;
    try {
      const emailCheckResponse = await AuthService.checkEmailExists(email);
      const data = emailCheckResponse?.data as { exists?: boolean; verified?: boolean; companyId?: string; companyName?: string } | undefined;
      if (data?.exists) {
        try {
          await AuthService.resendConfirmationCode(email);
          const companyId = data.companyId ?? '';
          const companyName = data.companyName ?? '';
          toast.success("We've sent a new verification code to your email.");
          const params = new URLSearchParams({
            planId: SIGNUP_WITHOUT_PAYMENT_FREE_TRIAL_PLAN_ID,
          });
          navigate(`/signup-verify-email?${params.toString()}`, {
            state: {
              email,
              password: formData.password,
              companyId,
              companyName,
            },
          });
          return false;
        } catch {
          setFormErrors((prev) => ({ ...prev, email: 'An account with this email already exists.' }));
          toast.error(
            'An account with this email already exists. Please use a different email or try logging in.'
          );
          return false;
        }
      }
      return true;
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not check email.');
      return false;
    }
  };

  const checkSubdomainAvailable = async (sub: string): Promise<boolean> => {
    if (!sub) return false;
    if (!/^[a-zA-Z0-9-]+$/.test(sub)) return false;
    try {
      const response = await AuthService.checkSubDomainAvailability(sub, Applications.EASYWEBINAR);
      if (!response.available) {
        setFormErrors((prev) => ({ ...prev, subdomain: 'This domain is already taken. Please try another one.' }));
        setSubdomainSuggestions(response.suggestions ?? []);
        toast.error('This domain is already taken. Please try another one.');
        return false;
      }
      return true;
    } catch (err: any) {
      setFormErrors((prev) => ({ ...prev, subdomain: err?.message ?? 'Could not check domain.' }));
      toast.error(err?.message ?? 'Could not check domain.');
      return false;
    }
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || isLoading) return;

    const emailTrimmed = formData.email.trim().toLowerCase();
    if (signupResult && signupResult.email === emailTrimmed) {
      const params = new URLSearchParams({
        planId: SIGNUP_WITHOUT_PAYMENT_FREE_TRIAL_PLAN_ID,
      });
      navigate(`/signup-verify-email?${params.toString()}`, {
        state: {
          email: signupResult.email,
          password: signupResult.password,
          companyId: signupResult.companyId,
          companyName: signupResult.companyName,
        },
      });
      toast.info('Please enter the confirmation code we sent to your email.');
      return;
    }

    setIsLoading(true);
    setFormErrors({});
    setSubdomainSuggestions([]);
    try {
      const emailOk = await handleCheckEmail();
      if (!emailOk) {
        setIsLoading(false);
        return;
      }

      let subdomainToUse = formData.subdomain.trim().toLowerCase();
      if (!subdomainToUse) {
        const base =
          [formData.firstName, formData.lastName].filter(Boolean).join('') ||
          formData.email.split('@')[0];
        const baseSubdomain = (base || '')
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '')
          .replace(/[^a-z0-9-]/g, '');
        if (!baseSubdomain) {
          toast.error('Enter your name so we can generate your domain.');
          setIsLoading(false);
          return;
        }
        try {
          const subdomainResponse = await AuthService.generateSubdomain(baseSubdomain, Applications.EASYWEBINAR);
          subdomainToUse = (subdomainResponse.subdomain ?? '').trim().toLowerCase();
          setFormData((prev) => ({ ...prev, subdomain: subdomainToUse }));
        } catch (err: any) {
          toast.error(err?.message ?? 'Could not generate domain.');
          setIsLoading(false);
          return;
        }
      }
      if (!subdomainToUse) {
        setFormErrors((prev) => ({ ...prev, subdomain: 'Domain is required' }));
        setIsLoading(false);
        return;
      }
      if (!/^[a-zA-Z0-9-]+$/.test(subdomainToUse)) {
        setFormErrors((prev) => ({ ...prev, subdomain: 'Domain can only contain letters, numbers, and hyphens' }));
        setIsLoading(false);
        return;
      }
      const subdomainOk = await checkSubdomainAvailable(subdomainToUse);
      if (!subdomainOk) {
        setIsLoading(false);
        return;
      }
      setSubdomainSuggestions([]);
      setFormErrors((prev) => ({ ...prev, subdomain: '' }));
    } catch {
      setIsLoading(false);
      return;
    }
    try {
      const payload: SignupWithoutPaymentRequest = {
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        firstName: sanitizeTextInput(formData.firstName.trim()),
        lastName: sanitizeTextInput(formData.lastName.trim()),
        planId: SIGNUP_WITHOUT_PAYMENT_FREE_TRIAL_PLAN_ID,
        phoneNumber: formData.phoneNumber.trim() ? sanitizeTextInput(formData.phoneNumber.trim()) : '',
        ...(formData.subdomain.trim() && { subdomain: formData.subdomain.trim().toLowerCase() }),
        ...(utmParams && Object.keys(utmParams).length > 0 && { utm_params: utmParams }),
      };
      const response = await signupWithoutPayment(payload);
      const companyId = response.companyId ?? '';
      const companyName = 'companyName' in response ? response.companyName ?? '' : '';
      const verifyState = {
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        companyId,
        companyName,
      };
      setSignupResult(verifyState);
      toast.success('Registration successful. Please check your email for the confirmation code.');
      const params = new URLSearchParams({
        planId: SIGNUP_WITHOUT_PAYMENT_FREE_TRIAL_PLAN_ID,
      });
      navigate(`/signup-verify-email?${params.toString()}`, { state: verifyState });
    } catch (err: any) {
      const isAlreadyExists =
        err?.message?.toLowerCase().includes('already exists') ||
        err?.message?.toLowerCase().includes('already registered');
      if (isAlreadyExists) {
        try {
          await AuthService.resendConfirmationCode(formData.email.trim().toLowerCase());
          toast.success("We've sent a new verification code to your email.");
          const params = new URLSearchParams({
            planId: SIGNUP_WITHOUT_PAYMENT_FREE_TRIAL_PLAN_ID,
          });
          navigate(`/signup-verify-email?${params.toString()}`, {
            state: {
              email: formData.email.trim().toLowerCase(),
              password: formData.password,
              companyId: '',
              companyName: '',
            },
          });
        } catch (resendErr: any) {
          toast.error('An account with this email already exists. Please use a different email or try logging in.');
        }
      } else {
        toast.error(err?.message ?? 'Signup failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueWithGoogle = () => {
    const authUrl = config.sso_auth_url;
    const stateData = {
      isRegistration: true,
      timestamp: Date.now(),
    };
    const params = new URLSearchParams({
      client_id: config.sso_client_id,
      response_type: 'code',
      scope: 'email openid profile phone',
      redirect_uri: `${window.location.origin}/auth/sso/callback`,
      state: JSON.stringify(stateData),
      identity_provider: 'Google',
      prompt: 'select_account',
    });
    window.location.href = `${authUrl}?${params.toString()}`;
  };

  const fieldProps = (name: string) => ({
    name,
    fullWidth: true as const,
    variant: 'outlined' as const,
    size: 'small' as const,
    error: !!formErrors[name],
    helperText: formErrors[name],
    onChange: handleInputChange,
    disabled: isLoading,
    margin: 'dense' as const,
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#FDF2F4',
        p: 1.5,
      }}
    >
      <Box sx={{ maxWidth: 480, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto' }}>
        <Box sx={{ textAlign: 'center', width: '100%', mb: 1.5 }}>
          <Typography
            component="h1"
            sx={{
              // fontFamily: 'inherit',
              fontSize: { xs: '1.75rem', sm: '2rem' },
              fontWeight: 700,
              color: '#2C303E',
              marginBottom: 0.5,
              lineHeight: 1.2,
            }}
          >
            Create your account
          </Typography>
          <Typography
            sx={{
              // fontFamily: 'inherit',
              fontSize: '1rem',
              fontWeight: 400,
              color: '#5B6171',
              lineHeight: 1.3,
            }}
          >
            Your AI webinar is just a step away
          </Typography>
        </Box>

        <Box
          sx={{
            width: '100%',
            maxWidth: 480,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            mb: 2,
            borderRadius: 4,
            bgcolor: '#F7F2FB',
            border: '1px solid rgba(155, 81, 224, 0.15)',
          }}
        >
          <BoltIcon sx={{ color: '#9B51E0', fontSize: 20, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.6875rem',
                fontWeight: 600,
                color: '#9B51E0',
                textTransform: 'uppercase',
                letterSpacing: '0.02em',
                lineHeight: 1.3,
              }}
            >
              YOUR AI PROMPT IS SAVED
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 400,
                color: '#3E424D',
                lineHeight: 1.4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
             {searchParams.get('try_ai_prompt')}
            </Typography>
          </Box>
          <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20, flexShrink: 0 }} />
        </Box>

        <Box
          sx={{
            maxWidth: 480,
            width: '100%',
            p: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            borderRadius: 4,
            bgcolor: '#ffffff',
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              minHeight: 44,
              '& .MuiOutlinedInput-input': { py: 1.25, fontSize: '1rem' },
              '& fieldset': { borderColor: '#D1D5DB' },
              '&:hover fieldset': { borderColor: '#9ca3af' },
              '&.Mui-focused': {
                boxShadow: 'none',
                '& fieldset': { borderColor: '#9B51E0', borderWidth: 1, boxShadow: 'none' },
              },
            },
            '& .MuiInputLabel-root': {
              color: '#9B51E0',
              fontSize: '1rem',
              fontWeight: 400,
              '&.Mui-focused': { color: '#9B51E0' },
            },
          }}
          component="form"
          onSubmit={handleSubmitForm}
        >
        {/* <Button
          type="button"
          fullWidth
          onClick={handleContinueWithGoogle}
          sx={{
            minHeight: 44,
            py: 1.25,
            borderRadius: 2,
            bgcolor: '#fff',
            color: '#3E424D',
            border: '1px solid #D1D5DB',
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            '&:hover': {
              bgcolor: '#fafafa',
              borderColor: '#bdbdbd',
            },
          }}
        >
          <GoogleIcon />
          Continue with Google
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
          <Box sx={{ flex: 1, height: 1, bgcolor: '#D1D5DB' }} />
          <Typography variant="body2" sx={{ color: '#888C95', fontSize: '0.875rem', fontWeight: 400, flexShrink: 0 }}>
            or sign up with email
          </Typography>
          <Box sx={{ flex: 1, height: 1, bgcolor: '#D1D5DB' }} />
        </Box> */}

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <TextField
            {...fieldProps('firstName')}
            label="First name"
            value={formData.firstName}
            autoComplete="given-name"
            sx={{ flex: 1, minWidth: 140 }}
          />
          <TextField
            {...fieldProps('lastName')}
            label="Last name"
            value={formData.lastName}
            autoComplete="family-name"
            sx={{
              flex: 1,
              minWidth: 140,
            }}
          />
        </Box>
        <TextField
          {...fieldProps('email')}
          label="Email"
          type="email"
          value={formData.email}
          autoComplete="email"
        />
        <TextField
          {...fieldProps('password')}
          label="Password"
          type="password"
          value={formData.password}
          autoComplete="new-password"
          helperText={formErrors.password || 'Min 8 chars, upper, lower, number, special character'}
        />
        {/* Phone number (optional) - commented out for now
        <Typography component="label" variant="body2" sx={{ display: 'block', mb: 0.5, mt: 0.75, fontSize: '1rem', fontWeight: 400, color: '#9B51E0' }}>
          Phone number (optional)
        </Typography>
        <MuiPhone
          value={formData.phoneNumber}
          onChange={handlePhoneChange}
          fullWidth
          error={!!formErrors.phoneNumber}
          helperText={formErrors.phoneNumber}
          disabled={isLoading}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              fontSize: '1rem',
              minHeight: 44,
              '& .MuiOutlinedInput-input': { py: 1.25 },
            },
            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D1D5DB' },
          }}
        />
        */}
        {(formErrors.subdomain || subdomainSuggestions.length > 0) && (
          <Box sx={{ mt: 1 }}>
            {formErrors.subdomain && (
              <Typography variant="body2" color="error" sx={{ mb: 0.5 }}>
                {formErrors.subdomain}
              </Typography>
            )}
            {subdomainSuggestions.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Suggestions:{' '}
                {subdomainSuggestions.map((s, i) => (
                  <React.Fragment key={s}>
                    {i > 0 && ', '}
                    <Typography
                      component="span"
                      color="primary"
                      sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => {
                        setFormData((prev) => ({ ...prev, subdomain: s }));
                        setSubdomainSuggestions([]);
                        setFormErrors((prev) => ({ ...prev, subdomain: '' }));
                      }}
                    >
                      {s}
                    </Typography>
                  </React.Fragment>
                ))}
              </Typography>
            )}
          </Box>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isLoading}
          endIcon={isLoading ? null : <ArrowForwardIcon />}
          sx={{
            mt: 1.5,
            py: 1.25,
            borderRadius: 2,
            bgcolor: '#9B51E0',
            color: '#FFFFFF',
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '1rem',
            '&:hover': { bgcolor: '#8B41D0' },
          }}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Create account'}
        </Button>

        <Typography variant="body2" sx={{ mt: 1.5, textAlign: 'center', fontSize: '0.8125rem', fontWeight: 400, color: '#888C95' }}>
          By signing up, you agree to our{' '}
          <Link href="https://easywebinar.com/terms-conditions/" target="_blank" rel="noopener noreferrer" sx={{ color: '#888C95', textDecoration: 'underline', fontWeight: 400 }}>
            Terms of Service
          </Link>
          {' '}and{' '}
          <Link href="https://easywebinar.com/privacy/" target="_blank" rel="noopener noreferrer" sx={{ color: '#888C95', textDecoration: 'underline', fontWeight: 400 }}>
            Privacy Policy
          </Link>
        </Typography>
        </Box>

        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', fontSize: '0.875rem', fontWeight: 400, color: '#888C95' }}>
          Already have an account?{' '}
          <Typography
            component="span"
            sx={{ color: '#9B51E0', cursor: 'pointer', fontWeight: 400, textDecoration: 'underline' }}
            onClick={() => navigate('/login-ai-flow')}
          >
            Log in
          </Typography>
        </Typography>
      </Box>
    </Box>
  );
};

export default SignupWithoutPayment;
